import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Inbox } from "lucide-react";
import AdminLogin from "./admin/AdminLogin";
import AdminHeader from "./admin/AdminHeader";
import ContactList from "./admin/ContactList";
import ContactDetail from "./admin/ContactDetail";
import { FORMATION_LABELS, SITUATION_LABELS, formatDate } from "./admin/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cfmpe_admin_token";

function authHeaders(token) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

function renderDetailPane({ selected, hasItems, onDelete }) {
  if (selected) {
    return <ContactDetail contact={selected} onDelete={onDelete} />;
  }
  if (hasItems) {
    return (
      <div className="hidden lg:flex items-center justify-center h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-[#e6ded0]">
        <p className="text-[#0e3a3f]/50 text-sm">
          ← Sélectionnez une demande pour voir le détail
        </p>
      </div>
    );
  }
  return null;
}

function exportItemsToCSV(items) {
  const headers = ["Date", "Prénom", "Nom", "Email", "Téléphone", "Formation", "Situation", "Message"];
  const rows = items.map((it) => [
    formatDate(it.created_at),
    it.firstName,
    it.lastName,
    it.email,
    it.phone,
    FORMATION_LABELS[it.formation] || it.formation || "",
    SITUATION_LABELS[it.situation] || it.situation || "",
    (it.message || "").replace(/\r?\n/g, " "),
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cfmpe-demandes-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setItems([]);
    setSelected(null);
  }, []);

  const fetchItems = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, authHeaders(tok));
      setItems(res.data.items || []);
      setAuthed(true);
      return true;
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const login = useCallback(async (password) => {
    setLoginError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const newToken = res.data.token;
      sessionStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      await fetchItems(newToken);
    } catch (err) {
      if (err.response?.status === 401) {
        setLoginError("Mot de passe incorrect");
      } else {
        setLoginError("Erreur de connexion au serveur");
      }
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [fetchItems, clearSession]);

  // Restore session on mount if a token is stored (validates it via GET)
  useEffect(() => {
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (saved) fetchItems(saved);
  }, [fetchItems]);

  const handleLogout = async () => {
    try {
      if (token) await axios.post(`${API}/admin/logout`, {}, authHeaders(token));
    } catch (err) {
      // Non-blocking: even if server-side revocation fails (offline, expired token),
      // we always clear local state below to log the user out client-side.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[Admin] Server logout failed, clearing local session anyway", err);
      }
    }
    clearSession();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeaders(token));
      setItems((prev) => prev.filter((it) => it.id !== id));
      setSelected((prev) => (prev?.id === id ? null : prev));
    } catch (err) {
      if (err.response?.status === 401) {
        clearSession();
      } else {
        alert("Erreur lors de la suppression.");
      }
    }
  };

  if (!authed) {
    return <AdminLogin onSubmit={login} error={loginError} loading={loading} />;
  }

  return (
    <div className="min-h-screen bg-[#fbfaf7]" data-testid="admin-dashboard">
      <AdminHeader
        loading={loading}
        onRefresh={() => fetchItems(token)}
        onExport={() => exportItemsToCSV(items)}
        onLogout={handleLogout}
        hasItems={items.length > 0}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white border border-[#e6ded0] shadow-sm">
            <Inbox className="w-4 h-4 text-[#2c7a80]" />
            <span className="text-sm text-[#0e3a3f]">
              <strong>{items.length}</strong> demande{items.length > 1 ? "s" : ""} au total
            </span>
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#e6ded0]">
            <Inbox className="w-12 h-12 text-[#2c7a80]/40 mx-auto mb-3" />
            <p className="text-[#0e3a3f]/60">Aucune demande reçue pour l&apos;instant.</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <ContactList
              items={items}
              selectedId={selected?.id}
              onSelect={setSelected}
            />
          </div>
          <div className="lg:col-span-3">
            {renderDetailPane({ selected, hasItems: items.length > 0, onDelete: handleDelete })}
          </div>
        </div>
      </div>
    </div>
  );
}
